import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useBasePath, joinNav } from '../../../lib/config/nav-helpers';
import { StatusBadge, QueryError } from '../../../components/ui';
import { LiveClock, localGreeting } from '../../../components/LiveClock';
import { AudioPlayButton } from '../../../components/AudioPlayButton';
import { api } from '../../../lib/api';
import { emit } from '../../../lib/analytics';
import type { MeetingPrepResult } from '../../../lib/meeting-prep';
import type { EscalationResult } from '../../../lib/escalation';
import type { WeeklyDigestResult } from '../../../lib/weekly-digest';
import type { RevenueForecast } from '../../../lib/revenue-forecast';
import { emitBriefingItem, type BriefingItem, type MlReadiness, type MorningBriefing } from '../../../lib/morning-briefing';
import { BriefingNarrative } from '../../../components/BriefingNarrative';
import { NO_LIVE_MODEL_COPY } from '../../../lib/ml-copy';
import { useLogBriefingView } from '../../../lib/briefing-view';
import { sees } from '../../../lib/coverage';

// ─── Helpers ────────────────────────────────────────────────────────

export function getNextMeeting(meetings: MeetingPrepResult | undefined): { summary: string; timeLabel: string } | null {
  if (!meetings?.meetings.length) return null;
  const now = Date.now();
  const upcoming = meetings.meetings
    .filter(m => new Date(m.startAt).getTime() > now)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  if (!upcoming.length) return null;
  const next = upcoming[0]!;
  const timeLabel = new Date(next.startAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return { summary: next.matchedClient ?? next.summary, timeLabel };
}

export function composeBriefing(
  escalationData: EscalationResult | undefined,
  meetingData: MeetingPrepResult | undefined,
  digestData: WeeklyDigestResult | undefined,
  revenueData: RevenueForecast | undefined,
): string {
  const pendingEscalations = escalationData?.actions.filter(a => a.status === 'pending') ?? [];
  const criticalCount = pendingEscalations.filter(a => a.severity === 'critical').length;
  const pendingCount = pendingEscalations.length;
  const nextMeeting = getNextMeeting(meetingData);
  // Alerts are drawn from email signals and overdue invoices: only with both visible is zero a finding. (Old API: no flag, no change.)
  const alertsSeen = escalationData?.pendingCount === undefined || escalationData.pendingCount !== null;

  // Find the most noteworthy silent/critical client for extra color
  const criticalItem = pendingEscalations.find(a => a.severity === 'critical');
  const criticalDetail = criticalItem?.targetEntity
    ? ` — ${criticalItem.targetEntity} went silent`
    : '';

  // Revenue highlight from digest
  const revDelta = digestData?.metricDeltas.find(d => d.metricId === 'rev');
  const revHighlight = revDelta && revDelta.isBetter && revDelta.deltaPct > 0
    ? `Revenue is ${Math.round(revDelta.deltaPct)}% ahead of pace.`
    : revenueData?.onTrack && sees(revenueData.coverage, 'money') // a claim about revenue needs a visible money source
      ? 'Revenue is on track.'
      : null;

  // Build the sentence
  const parts: string[] = [];

  if (revHighlight) {
    parts.push(revHighlight);
  }

  if (pendingCount > 0 && nextMeeting) {
    const itemWord = pendingCount === 1 ? 'item needs' : 'items need';
    parts.push(
      `${pendingCount} ${itemWord} your attention before your ${nextMeeting.timeLabel} with ${nextMeeting.summary}${criticalCount > 0 ? criticalDetail : ''}.`,
    );
  } else if (pendingCount > 0) {
    const escalationWord = pendingCount === 1 ? 'escalation' : 'escalations';
    parts.push(`${pendingCount} ${escalationWord} pending${criticalDetail}.`);
  } else if (!alertsSeen) {
    // "No alerts" is a claim about email and billing: without them, say only what the calendar shows.
    parts.push(nextMeeting ? `Your next meeting is at ${nextMeeting.timeLabel}.` : 'No upcoming meetings.');
  } else if (nextMeeting) {
    parts.push(`Clean ${localGreeting().includes('morning') ? 'morning' : 'slate'} — no critical alerts. Your next meeting is at ${nextMeeting.timeLabel}.`);
  } else {
    parts.push('No critical alerts and no upcoming meetings. A good time to focus on strategic work.');
  }

  // Append a concern from digest if we haven't already mentioned escalations in detail
  if (digestData?.concerns.length && !criticalDetail) {
    const concern = digestData.concerns[0]!;
    const first = concern.split(/[.!]/)[0];
    if (first && !parts.some(p => p.includes(first.slice(0, 20)))) {
      parts.push(first + '.');
    }
  }

  return parts.join(' ');
}

/**
 * The empty-briefing line. A quiet day says so and when the data was last synced, so it
 * reads as a result and not as a broken page; nothing connected says that instead.
 */
export function quietDayLine(status: 'items' | 'quiet' | 'no_data' | undefined, dataAsOf: string | null | undefined): string {
  if (status === 'no_data') return 'Nothing is connected yet. Connect your CRM or accounting tool to start your briefing.';
  const asOf = dataAsOf ? ` Built on data synced ${new Date(dataAsOf).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.` : '';
  return `Quiet day: nothing needs you, and this is a real check, not a failed one.${asOf}`;
}

/** Tiny inline component showing when data was last synced from integrations. */
export function SyncFreshness() {
  const syncQ = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => api<{ lastFullSync: string | null }>('/v1/sync-status'),
    staleTime: 120_000,
    retry: false,
  });
  if (!syncQ.data?.lastFullSync) return null;
  const ago = Date.now() - new Date(syncQ.data.lastFullSync).getTime();
  const mins = Math.floor(ago / 60_000);
  const label = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ago`;
  return (
    <span className="text-ink-dim/60" title={`Last sync: ${new Date(syncQ.data.lastFullSync).toLocaleString()}`}>
      · synced {label}
    </span>
  );
}

// ─── Props ───────────────────────────────────────────────────────────

export interface MorningBriefingCardProps {
  briefing: { data?: { summary?: string; items: BriefingItem[]; mlReadiness?: MlReadiness; status?: 'items' | 'quiet' | 'no_data'; dataAsOf?: string | null; narrative?: MorningBriefing['narrative']; narrativeNotice?: string; briefingId?: string }; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
  briefingExpanded: boolean;
  setBriefingExpanded: (v: boolean) => void;
  revenue: { data?: RevenueForecast };
  meetings: { data?: MeetingPrepResult };
  digest: { data?: WeeklyDigestResult };
  escalations: { data?: EscalationResult };
  followUps: { data?: any };
  dealRisk: { data?: any };
  liveStatus: string;
  firstName: string;
}

// ─── Component ───────────────────────────────────────────────────────

export function MorningBriefingCard({
  briefing,
  briefingExpanded,
  setBriefingExpanded,
  revenue,
  meetings,
  digest,
  escalations,
  liveStatus,
  firstName,
}: MorningBriefingCardProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  useLogBriefingView(briefing.data, 'web');
  // Dismissed items leave the card for this visit; the event is the record.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  const dataReady = !!(escalations.data || meetings.data || digest.data);
  const briefingLine = composeBriefing(escalations.data, meetings.data, digest.data, revenue.data);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border p-6" style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 6%, transparent), color-mix(in srgb, var(--primary-bright) 4%, transparent), color-mix(in srgb, var(--warn) 3%, transparent))' }}>
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute -left-24 -bottom-24 h-48 w-48 rounded-full bg-primary-bright/5 blur-3xl" />
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-ink">{localGreeting()}, {firstName.charAt(0).toUpperCase() + firstName.slice(1)}</h1>
            {liveStatus === 'open' && (
              <span title="Connected to live updates — dashboard refreshes automatically the moment underlying data changes." className="inline-flex items-center gap-1.5 rounded-full bg-success/10 border border-success/30 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-success">
                <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                Live
              </span>
            )}
            {liveStatus === 'connecting' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-dim/10 border border-border px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-ink-dim">
                <span className="h-1.5 w-1.5 rounded-full bg-ink-dim animate-pulse" />
                Connecting
              </span>
            )}
          </div>
          <p className="mt-0.5 text-2xs text-ink-dim font-mono tabular-nums flex items-center gap-2">
            <LiveClock format="full" />
            <SyncFreshness />
          </p>
          {briefing.data?.items.some((i) => i.openLikelihood != null) && (
            <p className="mt-0.5 text-2xs text-ink-dim" title="From your workspace's own model of which briefing items you open">
              Ordered by what you usually open first
            </p>
          )}
        </div>
        <div className="flex items-start gap-2">
          <AudioPlayButton endpoint="/v1/audio/briefing" label="Listen" />
          <button
            onClick={() => {
              setBriefingExpanded(!briefingExpanded);
              // Collapse only. The card renders expanded (usePreference default
              // true), so the other arm of this used to be `briefing_viewed` and
              // needed a re-expand to fire: 0 rows in five months. The real
              // "they read it" signal is useLogBriefingView above, which writes
              // interaction_log.briefing_view on render — that is what the
              // activation spine reads.
              if (briefingExpanded) emit('briefing_dismissed');
            }}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-2xs font-medium text-ink-dim hover:bg-surface-2 hover:text-ink transition-colors"
          >
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={clsx('transition-transform', briefingExpanded ? 'rotate-180' : '')}>
              <path d="M6 9l6 6 6-6" />
            </svg>
            {briefingExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>
      {briefing.isError ? (
        <div className="mt-2"><QueryError error={briefing.error} onRetry={() => briefing.refetch()} compact /></div>
      ) : (
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted">
          {briefing.data?.summary ?? (dataReady ? briefingLine : 'Your intelligence briefing is loading…')}
        </p>
      )}
      {briefing.data?.mlReadiness && briefing.data.mlReadiness.status !== 'ready' && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-2xs text-ink-dim">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary animate-pulse" />
          {briefing.data.mlReadiness.modelsWithData === 0
            ? NO_LIVE_MODEL_COPY
            : `${briefing.data.mlReadiness.modelsWithData}/${briefing.data.mlReadiness.totalCoreModels} models live — the others activate once they beat a baseline on your data.`}
        </div>
      )}
      {briefingExpanded && briefing.data && briefing.data.items.length === 0 && (
        <div className="mt-4 rounded-lg bg-surface-1 px-3 py-2.5 text-2xs text-ink-dim">
          {quietDayLine(briefing.data.status, briefing.data.dataAsOf)}
        </div>
      )}
      {briefingExpanded && briefing.data && briefing.data.items.length > 0 && (
        <div className="mt-4 space-y-2">
          {briefing.data.items.filter((item) => !dismissed.has(item.id)).map((item: BriefingItem) => (
            <div key={item.id} className="flex items-start gap-3 rounded-lg bg-surface-1 px-3 py-2.5 backdrop-blur-xs">
              <span className={clsx('mt-1.5 h-2 w-2 shrink-0 rounded-full', item.priority === 'high' ? 'bg-danger' : item.priority === 'medium' ? 'bg-warn' : 'bg-ink-dim/40')} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-ink leading-relaxed">{item.message}</p>
              </div>
              {item.actionLabel && item.actionTarget && (
                <button
                  onClick={() => {
                    emitBriefingItem('briefing_item_opened', briefing.data?.briefingId, item);
                    navigate(joinNav(basePath, item.actionTarget));
                  }}
                  className="shrink-0 rounded-lg bg-primary/10 px-2.5 py-1 text-2xs font-semibold text-primary transition-colors hover:bg-primary/20"
                >
                  {item.actionLabel}
                </button>
              )}
              <button
                onClick={() => {
                  emitBriefingItem('briefing_item_dismissed', briefing.data?.briefingId, item);
                  setDismissed((s) => new Set(s).add(item.id));
                }}
                aria-label="Dismiss"
                title="Dismiss"
                className="shrink-0 rounded-lg p-1 text-ink-dim transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
      {briefingExpanded && briefing.data && (briefing.data.narrative || briefing.data.narrativeNotice) && (
        <div className="mt-4"><BriefingNarrative narrative={briefing.data.narrative} notice={briefing.data.narrativeNotice} /></div>
      )}
      {!briefingExpanded && digest.data && digest.data.concerns.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <StatusBadge status="warning" label="Needs attention" size="xs" />
          <span className="text-2xs text-ink-dim">{digest.data.concerns[0]}</span>
        </div>
      )}
    </div>
  );
}
